/* The grooming editor (designs/intake-groom.md IG4): the score strip with
   the formula's parts spelled out, above the grooming fields, saved as a
   diff-only tracking PUT (plus the record PUT for groom-side priority,
   UF13). It lives in the effort's own drawer, on the queue and on the Groom
   board alike (IG8). */
import { useState, type JSX } from "react";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import { OwnersField } from "./OwnersField";
import {
  EFFORTS,
  EMPTY_TRACKING,
  HEALTHS,
  PLATFORM_STATUSES,
  PRIORITIES,
  REJECTION_REASON_REQUIRED,
  computeScore,
  labelFor,
  reach,
  type CapabilityBoardRow,
  type TrackingPatch,
} from "../../../shared/capabilities";

const asJson = async <T,>(res: Response): Promise<T> => {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
};

export function GroomEditor({
  row,
  title,
  teams = [],
  onSaved,
  onClose,
}: {
  row: CapabilityBoardRow;
  /* the heading; inside the effort's drawer the effort is already named */
  title?: string;
  /* the owners field's team list (intake-flow-review.md FR2) */
  teams?: CanonicalTeam[];
  onSaved: () => void;
  onClose: () => void;
}): JSX.Element {
  const t = row.tracking;
  /* groom-side priority (UF13): lives on the RECORD, not the tracking
     stream, so it saves through the capability PUT beside the tracking PUT */
  const [priority, setPriority] = useState(row.capability.priority ?? "");
  const [platformStatus, setPlatformStatus] = useState(t?.platformStatus ?? "triage");
  const [effort, setEffort] = useState(t?.effort ?? "");
  const [groomed, setGroomed] = useState(t?.groomed ?? false);
  const [committed, setCommitted] = useState(t?.committed ?? false);
  const [health, setHealth] = useState(t?.health ?? "");
  const [notes, setNotes] = useState(t?.platformNotes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* FR2: the owner is decided here. A requester's suggestion on an effort
     nobody has groomed is CONFIRMED or changed, never inherited — routing
     is groomed AND owned, so a silent groom would route to it */
  const standingOwners = row.capability.owners ?? null;
  const [owners, setOwners] = useState<string[] | null>(standingOwners);
  const [confirmed, setConfirmed] = useState(false);
  const suggestion = t?.groomed !== true && (standingOwners?.length ?? 0) > 0;
  const ownersChanged = JSON.stringify(owners) !== JSON.stringify(standingOwners);
  const teamName = (id: string): string => teams.find((tm) => tm.id === id)?.name ?? id;

  const save = async (): Promise<void> => {
    if (suggestion && !ownersChanged && !confirmed)
      return void setError(
        "Confirm the suggested owner, change it, or mark it not yet decided — grooming routes the effort to its owner.",
      );
    if (platformStatus === "rejected" && notes.trim() === "")
      return void setError(REJECTION_REASON_REQUIRED);
    setBusy(true);
    setError(null);
    /* the patch carries EXACTLY the changed fields (IG4): a no-op save
       must never mint a revision or re-stamp the record. The baseline's
       null status reads as "triage" — the editor preselects it and the
       lanes fold the two together, so it is not a change either. */
    const standing = t ?? EMPTY_TRACKING;
    const baseline = { ...standing, platformStatus: standing.platformStatus ?? "triage" };
    const next = {
      platformStatus,
      effort: effort === "" ? null : effort,
      /* confirming the suggestion IS the groom */
      groomed: groomed || confirmed,
      committed,
      health: health === "" ? null : health,
      platformNotes: notes.trim() === "" ? null : notes,
    } as const;
    const patch: TrackingPatch = {};
    for (const key of Object.keys(next) as (keyof typeof next)[])
      if (next[key] !== baseline[key]) (patch as Record<string, unknown>)[key] = next[key];
    const nextPriority = priority === "" ? null : priority;
    const priorityChanged = nextPriority !== (row.capability.priority ?? null);
    if (Object.keys(patch).length === 0 && !priorityChanged && !ownersChanged) {
      onClose();
      return;
    }
    try {
      if (priorityChanged || ownersChanged)
        await asJson(
          await fetch(`/api/intake/capabilities/${row.capability.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...(priorityChanged ? { priority: nextPriority } : {}),
              ...(ownersChanged ? { owners } : {}),
            }),
          }),
        );
      if (Object.keys(patch).length > 0)
        await asJson(
          await fetch(`/api/intake/capabilities/${row.capability.id}/tracking`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
          }),
        );
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  /* the score strip spells the formula out (IG4) */
  const scorable = {
    priority: priority === "" ? null : priority,
    scope: row.capability.scope,
    stakeholderCount: row.stakeholderCount,
    effort: effort === "" ? null : effort,
  };
  return (
    <div className="groom-editor" data-groom-editor>
      <div className="intake-drawer-head">
        <h3>{title ?? row.capability.name}</h3>
        <button aria-label="Close grooming" onClick={onClose}>×</button>
      </div>
      <p className="groom-score" data-groom-score>
        Score <b>{computeScore(scorable)}</b> = value ({priority === "" ? "not prioritised — neutral" : labelFor(PRIORITIES, priority)})
        × reach ({reach(scorable)} = submitter + {row.stakeholderCount} +1
        {row.stakeholderCount === 1 ? "" : "s"}
        {row.capability.scope === "common" ? " + common-scope nudge" : ""}) ÷ effort (
        {effort === "" ? "M assumed — size it" : labelFor(EFFORTS, effort)})
      </p>
      <div className="capture-grid">
        {suggestion && (
          <div className="capture-field capture-wide groom-owner-suggestion" data-owner-suggested>
            <span>Owner</span>
            <p>
              Suggested by the requester:{" "}
              <b>{(standingOwners ?? []).map(teamName).join(", ")}</b>. Grooming routes the effort
              to its owner, so confirm the suggestion, change it below, or mark it not yet decided.
            </p>
            <label className={confirmed ? "capture-check on" : "capture-check"}>
              <input type="checkbox" checked={confirmed} onChange={() => setConfirmed(!confirmed)} />
              Confirm the suggested owner
            </label>
          </div>
        )}
        <OwnersField teams={teams} value={owners} onChange={setOwners} />
        <label className="capture-field">
          <span>Priority</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="">— not prioritised —</option>
            {PRIORITIES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="capture-field">
          <span>Platform status</span>
          <select value={platformStatus} onChange={(e) => setPlatformStatus(e.target.value)}>
            {PLATFORM_STATUSES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="capture-field">
          <span>Effort</span>
          <select value={effort} onChange={(e) => setEffort(e.target.value)}>
            <option value="">— not sized —</option>
            {EFFORTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="capture-field">
          <span>Health</span>
          <select value={health} onChange={(e) => setHealth(e.target.value)}>
            <option value="">— not set —</option>
            {HEALTHS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="capture-field">
          <span>Flags</span>
          <div className="capture-checks">
            <label className={groomed ? "capture-check on" : "capture-check"}>
              <input type="checkbox" checked={groomed} onChange={() => setGroomed(!groomed)} />
              Groomed
            </label>
            <label className={committed ? "capture-check on" : "capture-check"}>
              <input type="checkbox" checked={committed} onChange={() => setCommitted(!committed)} />
              Committed
            </label>
          </div>
        </div>
        <label className="capture-field capture-wide">
          <span>Platform notes</span>
          <textarea
            value={notes}
            placeholder={platformStatus === "rejected" ? "Why it is rejected — required" : undefined}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </label>
      </div>
      {error !== null && <p className="capture-error">{error}</p>}
      <button className="capture-submit" disabled={busy} onClick={() => void save()}>
        Save grooming
      </button>
    </div>
  );
}
